import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Lock, ArrowRight, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Day 1 Completion Gate
const Day1CompletionGate = ({ user }) => {
  const navigate = useNavigate();
  const [day1Data, setDay1Data] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchDay1Data();
  }, []);

  const fetchDay1Data = async () => {
    try {
      const response = await axios.get(`${API}/journey/day/1`, { withCredentials: true });
      setDay1Data(response.data.form_data || {});
    } catch (error) {
      console.error('Error fetching Day 1 data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error("Failed to start payment");
    } finally {
      setProcessingPayment(false);
    }
  };

  // Get the persona/pattern if available
  const coreOpportunity = day1Data?.core_opportunity;
  const topPainPoint = coreOpportunity?.winning_pain_point || 
    day1Data?.pain_matrix?.find(p => p.pain?.trim())?.pain;
  
  // Determine persona type based on pain points
  const getPersonaType = () => {
    if (!day1Data?.pain_matrix) return null;
    const filledPains = day1Data.pain_matrix.filter(p => p.pain?.trim()).length;
    if (filledPains >= 10) return "The Visionary Builder";
    if (filledPains >= 7) return "The Faithful Overthinker";
    if (filledPains >= 4) return "The Ready Founder";
    return "The Emerging Dreamer";
  };

  const personaType = getPersonaType();

  const lockedDays = [
    { day: 2, title: "Strengths Audit", desc: "Discover your unfair advantage" },
    { day: 3, title: "Idea Generation", desc: "One ranked, committed idea" },
    { day: 4, title: "MVP Scope", desc: "Strip it to what actually ships" },
    { day: 5, title: "Landing Page Copy", desc: "Written from your data" },
    { day: 6, title: "Build Your Page", desc: "Live, shareable, no coding" },
    { day: 7, title: "Validation and Decision", desc: "GO, PIVOT, or RELEASE" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0908] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0908] py-12 px-4" data-testid="day1-completion-gate">
      <div className="max-w-2xl mx-auto">
        {/* Day 1 Summary */}
        {(topPainPoint || personaType) && (
          <Card 
            className="mb-8 p-6"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.07) 0%, rgba(212,175,55,0.02) 100%)',
              border: '1px solid rgba(212,175,55,0.3)',
              borderRadius: '16px'
            }}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#d4af37] flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-black" />
              </div>
              <div>
                {personaType && (
                  <span 
                    className="inline-block mb-3"
                    style={{
                      background: 'rgba(212,175,55,0.2)',
                      color: '#d4af37',
                      borderRadius: '20px',
                      padding: '4px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    {personaType}
                  </span>
                )}
                {topPainPoint && (
                  <>
                    <p className="text-white/60 text-sm mb-1">Your top identified opportunity:</p>
                    <p className="text-white font-medium text-lg">{topPainPoint}</p>
                  </>
                )}
                {!topPainPoint && !personaType && (
                  <p className="text-white/60">
                    Your pattern is ready. Day 2 will reveal it.
                  </p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Main Headline */}
        <div className="text-center mb-10">
          <h1 
            className="text-white font-bold mb-4"
            style={{ fontSize: '2.5rem', lineHeight: 1.1 }}
          >
            You found it.<br />
            Now let us build it.
          </h1>
          <p 
            style={{ color: '#d4af37', fontSize: '1.1rem' }}
          >
            Day 2 is where we find out if you are the right person to build this idea. Your Day 1 work carries forward.
          </p>
        </div>

        {/* Locked Day Previews */}
        <div className="space-y-3 mb-10">
          {lockedDays.map((item) => (
            <div 
              key={item.day}
              className="flex items-center gap-4 p-4 rounded-lg"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(212,175,55,0.1)',
                borderRadius: '8px'
              }}
              data-testid={`locked-day-${item.day}`}
            >
              <Lock className="w-5 h-5 text-white/30 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[#d4af37] text-sm font-medium">Day {item.day}:</span>
                  <span className="text-white font-medium">{item.title}</span>
                </div>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Primary CTA */}
        <Button 
          onClick={handleUnlock}
          disabled={processingPayment}
          className="w-full py-6 rounded-lg font-bold text-base mb-4"
          style={{
            background: '#d4af37',
            color: '#0a0908'
          }}
          data-testid="unlock-journey-btn"
        >
          {processingPayment ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              Processing...
            </span>
          ) : (
            <>Unlock Your Full Journey — $97</>
          )}
        </Button>

        {/* Fine Print */}
        <p className="text-center text-white/40 text-sm mb-6">
          One-time payment. Lifetime access. Your Day 1 results carry forward automatically.
        </p>

        {/* Secondary Link */}
        <div className="text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-white/30 text-[0.85rem] hover:text-white/50 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default Day1CompletionGate;
